import { Controller } from "@hotwired/stimulus"

// Controller para pagamentos MB WAY
// Uso: data-controller="mbway-payment"
export default class extends Controller {
  static targets = ["phone", "amount", "message", "anonymous", "submitButton", "status", "form"]
  static values = {
    campaignId: Number,
    token: String,
    checkInterval: { type: Number, default: 3000 }
  }

  connect() {
    this.polling = false
    this.paymentId = null
  }

  disconnect() {
    this.stopPolling()
  }

  async submit(event) {
    event.preventDefault()

    const phone = this.phoneTarget.value.replace(/\D/g, "")
    const amount = this.amountTarget.value
    const message = this.hasMessageTarget ? this.messageTarget.value : ""
    const anonymous = this.hasAnonymousTarget ? this.anonymousTarget.checked : false

    // Validação básica
    if (!this.validatePhone(phone)) {
      this.showError("Por favor, insira um número de telemóvel válido (9XXXXXXXX)")
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      this.showError("Por favor, insira um valor válido")
      return
    }

    // Desativar botão
    this.setLoading(true)

    try {
      const response = await fetch(`/p/${this.tokenValue}/mbway/${this.campaignIdValue}/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          phone: phone,
          amount: amount,
          message: message,
          anonymous: anonymous
        })
      })

      const data = await response.json()

      if (data.success) {
        this.paymentId = data.payment_id
        this.showWaitingForApproval()
        this.startPolling()
      } else {
        this.showError(data.error || "Erro ao processar pagamento")
        this.setLoading(false)
      }
    } catch (error) {
      console.error("Erro MB WAY:", error)
      this.showError("Erro de comunicação. Tente novamente.")
      this.setLoading(false)
    }
  }

  validatePhone(phone) {
    // Número português: 9X XXX XX XX (9 dígitos começando por 9)
    const cleaned = phone.replace(/^351/, "")
    return /^9[1236]\d{7}$/.test(cleaned)
  }

  showWaitingForApproval() {
    if (this.hasFormTarget) {
      this.formTarget.classList.add("hidden")
    }
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="animate-pulse mb-4">
            <svg class="w-16 h-16 mx-auto text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold mb-2">Verifique o seu telemóvel</h3>
          <p class="text-gray-600">Aprove o pagamento na app MB WAY</p>
          <p class="text-sm text-gray-400 mt-4">A aguardar confirmação...</p>
        </div>
      `
      this.statusTarget.classList.remove("hidden")
    }
  }

  showSuccess() {
    this.stopPolling()
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="mb-4">
            <svg class="w-16 h-16 mx-auto text-green-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-green-600 mb-2">Pagamento confirmado!</h3>
          <p class="text-gray-600">Obrigado pela sua contribuição 🎉</p>
          <a href="javascript:location.reload()" class="mt-4 inline-block text-blue-500 hover:underline">
            Continuar
          </a>
        </div>
      `
    }
  }

  showError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p class="text-red-600">${message}</p>
        </div>
      `
      this.statusTarget.classList.remove("hidden")
    } else {
      alert(message)
    }
  }

  showExpired() {
    this.stopPolling()
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="text-center py-8">
          <div class="mb-4">
            <svg class="w-16 h-16 mx-auto text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-yellow-600 mb-2">Tempo expirado</h3>
          <p class="text-gray-600">O pagamento não foi aprovado a tempo.</p>
          <button onclick="location.reload()" class="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Tentar novamente
          </button>
        </div>
      `
    }
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      this.submitButtonTarget.innerHTML = loading
        ? '<span class="animate-spin">⏳</span> A processar...'
        : 'Pagar com MB WAY'
    }
  }

  startPolling() {
    if (this.polling) return
    this.polling = true
    this.pollCount = 0
    this.maxPolls = 40 // 2 minutos max (40 * 3s)

    this.pollInterval = setInterval(() => this.checkPaymentStatus(), this.checkIntervalValue)
  }

  stopPolling() {
    this.polling = false
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async checkPaymentStatus() {
    if (!this.paymentId || !this.polling) return

    this.pollCount++
    if (this.pollCount > this.maxPolls) {
      this.showExpired()
      return
    }

    try {
      const response = await fetch(`/p/${this.tokenValue}/mbway/${this.paymentId}/status`)
      const data = await response.json()

      if (data.paid) {
        this.showSuccess()
      } else if (data.finalized && !data.paid) {
        this.showExpired()
      }
      // Se ainda está a processar, continua polling
    } catch (error) {
      console.warn("Erro ao verificar status:", error)
      // Continuar a tentar
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
